import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { constructReportedQuestionPayload } from '../utils/PayloadUtils';
import { reportedQuestionPostUrl, reportedQuestionPutUrl } from '../utils/UrlUtilities';

const ReportQuestionDropdown = ({ questionId, questionReportDetails, onClose }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const CallApi = useApiCaller(); // Use the api caller
    
    // Initialize with default values
    const [reportReasons, setReportReasons] = useState({
        reason_wrong_text: false,
        reason_wrong_answer: false,
        reason_bad_question: false,
        reason_out_of_syllabus: false,
        reason_other: false
    });
    
    const [comment, setComment] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    
    // Update state when questionReportDetails changes. The status entry uses
    // `is_reported` to decide whether this question is already reported, and
    // carries the real reason booleans + comment for prefill.
    useEffect(() => {
        if (questionReportDetails && questionReportDetails.is_reported) {
            setReportReasons({
                reason_wrong_text: !!questionReportDetails.reason_wrong_text,
                reason_wrong_answer: !!questionReportDetails.reason_wrong_answer,
                reason_bad_question: !!questionReportDetails.reason_bad_question,
                reason_out_of_syllabus: !!questionReportDetails.reason_out_of_syllabus,
                reason_other: !!questionReportDetails.reason_other
            });
            setComment(questionReportDetails.comment || '');
            setIsSubmitted(true);
        } else {
            setReportReasons({
                reason_wrong_text: false,
                reason_wrong_answer: false,
                reason_bad_question: false,
                reason_out_of_syllabus: false,
                reason_other: false
            });
            setComment('');
            setIsSubmitted(false);
        }
    }, [questionReportDetails]);

    const handleCheckboxChange = (reason) => {
        setReportReasons(prev => ({
            ...prev,
            [reason]: !prev[reason]
        }));
    };

    const handleSubmit = async () => {
        try {
            const commentForPayload = comment.trim() === '' ? null : comment;
            const reportedQuestionUrl = reportedQuestionPostUrl();
            const reportedQuestionPostPayload = constructReportedQuestionPayload(questionId, reportReasons, commentForPayload);
            const response = await CallApi({
                url: reportedQuestionUrl,
                method: 'POST',
                token: true,
                payload: reportedQuestionPostPayload
            });

            if (response.success) {
                setIsOpen(false); // Close dropdown on success
                setIsSubmitted(true);
            } else {
                console.log('Failed to report question: ' + (response.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error reporting question:', error);
        }
    };

    const handleRevoke = async (reportId) => {
        try {
            const reportedQuestionDeleteUrl = reportedQuestionPutUrl(reportId);
            const response = await CallApi({
                url: reportedQuestionDeleteUrl,
                method: 'DELETE',
                token: true,
                payload: null
            });

            if (response.success) {
                setIsOpen(false); // Close dropdown on success
                setIsSubmitted(false);
                setReportReasons({
                    reason_wrong_text: false,
                    reason_wrong_answer: false,
                    reason_bad_question: false,
                    reason_out_of_syllabus: false,
                    reason_other: false
                });
                setComment('');
            } else {
                console.log('Failed to report question: ' + (response.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error reporting question:', error);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
            <button className="btn"
                onClick={() => setIsOpen(!isOpen)}
                style={{ backgroundColor: isSubmitted ? '#d65355' : '#e5e7eb', color: isSubmitted ? '#ffffff' : 'black', padding: '0.3rem 0.6rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
            >
                {isSubmitted ? 'Reported' : 'Report'} <span style={{ fontSize: '0.5rem' }}>▼</span>
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    right: 0,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    minWidth: 'min(250px, 90vw)',
                    maxWidth: '90vw',
                    zIndex: 10,
                    border: '1px solid #e5e7eb',
                    top: 'calc(100% + 0.5rem)' // Position below the button
                }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.75rem', color: '#374151' }}>Reason for reporting:</h4>
                    <div style={{ marginBottom: '0.75rem' }}>
                        {Object.keys(reportReasons).map((reasonKey) => (
                            <div key={reasonKey} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem' }}>
                                <input
                                    type="checkbox"
                                    id={`${questionId}-${reasonKey}`}
                                    checked={reportReasons[reasonKey]}
                                    onChange={() => handleCheckboxChange(reasonKey)}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <label htmlFor={`${questionId}-${reasonKey}`} style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                                    {reasonKey.replace(/_/g, ' ').replace('reason', '').trim().replace(/\b\w/g, char => char.toUpperCase())}
                                </label>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor={`comment-${questionId}`} style={{ display: 'block', fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#374151' }}>Comment (optional):</label>
                        <textarea
                            id={`comment-${questionId}`}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows="3"
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                resize: 'vertical'
                            }}
                            placeholder="Provide additional details..."
                        ></textarea>
                    </div>
                    <button
                        onClick={handleSubmit}
                        style={{
                            marginBottom: '0.5rem',
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            padding: '0.6rem 1rem',
                            borderRadius: '0.375rem',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >
                        {isSubmitted ? 'Update' : 'Submit'} Report
                    </button>
                    <button
                        onClick={() => handleRevoke(questionReportDetails.report_id)}
                        style={{
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            padding: '0.6rem 1rem',
                            borderRadius: '0.375rem',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >
                        Revoke Report
                    </button>
                </div>
                
            )}
        </div>
    );
};

export default ReportQuestionDropdown;
